/**
 * iter176 - PWA Install Prompt (Feb 2026)
 * User asked: kiddoplus.com has an "Install Kiddo+" popup. Add the same
 * to SpectraLearn.
 *
 * Behaviour:
 *   - Listen for `beforeinstallprompt` (Chrome / Edge / Android). When it
 *     fires we stash the event and reveal our card.
 *   - iOS Safari does not fire the event. If we detect iOS in standalone-
 *     capable Safari AND the user is not already in standalone mode, we
 *     show the same card with an "Add to Home Screen" hint instead of the
 *     Install button.
 *   - "Not now" and hard dismiss set localStorage flags so we do not
 *     re-nag the same user. Auto-dismisses if `appinstalled` fires.
 *
 * Style matches Kiddo+ screenshot: white rounded card, brand mark left,
 * headline + body copy centre, "Install" (coral pill) + "Not now" (ghost).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const LS_KEY = "spectralearn_pwa_install_dismissed_v1";
const LS_INSTALLED = "spectralearn_pwa_installed_v1";
// iter180 - Page-view counter so we only show the install prompt after
// the visitor has clicked around a bit (>= 3 views). First-visit noise
// hurts retention.
const LS_PV = "spectralearn_pwa_pv_v1";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPhone|iPad|iPod/i.test(ua);
  const webkit = /WebKit/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  return iOS && webkit;
}

export default function PWAInstallPrompt() {
  const [event, setEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isStandalone()) return undefined;
    // Respect prior dismissal for 7 days.
    try {
      const raw = window.localStorage.getItem(LS_KEY);
      if (raw) {
        const t = parseInt(raw, 10);
        if (!Number.isNaN(t) && Date.now() - t < 7 * 24 * 3600 * 1000) return undefined;
      }
    } catch (e) { /* localStorage disabled - fall through */ }

    // iter180 - Only nudge users with real intent. Increment a page-view
    // counter on every mount, and hold the prompt until at least 3 views
    // have accumulated. Prevents first-visit noise, boosts install-conversion.
    let pvCount = 0;
    try {
      pvCount = parseInt(window.localStorage.getItem(LS_PV) || "0", 10) || 0;
      pvCount += 1;
      window.localStorage.setItem(LS_PV, String(pvCount));
    } catch (e) { /* ignore */ }
    const hasEngagement = pvCount >= 3;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setEvent(e);
      // Only nudge if the user has actual engagement (iter180).
      if (!hasEngagement) return;
      setTimeout(() => setVisible(true), 3500);
    };
    const handleInstalled = () => {
      try { window.localStorage.setItem(LS_INSTALLED, "1"); } catch (e) { /* ignore */ }
      setVisible(false);
      setEvent(null);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleInstalled);

    // iOS Safari: no beforeinstallprompt. Show the card with an iOS hint.
    if (isIOSSafari() && hasEngagement) {
      const t = setTimeout(() => { setIosHint(true); setVisible(true); }, 5000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
        window.removeEventListener("appinstalled", handleInstalled);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    try { window.localStorage.setItem(LS_KEY, String(Date.now())); } catch (e) { /* ignore */ }
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    if (!event) { dismiss(); return; }
    try {
      event.prompt();
      const choice = await event.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
        setEvent(null);
      } else {
        dismiss();
      }
    } catch (e) {
      dismiss();
    }
  }, [event, dismiss]);

  if (!visible) return null;

  return (
    <div
      className="fixed z-[95] left-1/2 -translate-x-1/2 bottom-6 w-[min(560px,calc(100vw-24px))] px-3 sm:bottom-8"
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-labelledby="pwa-install-title"
    >
      <div
        className="rounded-2xl border bg-white p-4 pl-3 shadow-[0_20px_50px_-15px_rgba(27,31,59,0.35)] flex items-start gap-3"
        style={{ borderColor: "#F2D9C6" }}
      >
        {/* Brand mark */}
        <div
          className="shrink-0 h-11 w-11 rounded-xl flex items-center justify-center overflow-hidden"
          style={{ background: "#FFE7D6" }}
          aria-hidden="true"
        >
          <img
            src="/brand/spectralearn-mark-80.png"
            alt=""
            width={44} height={44}
            className="h-11 w-11 object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div id="pwa-install-title" className="text-[15px] font-black" style={{ color: "#1B1F3B" }}>
              Install SpectraLearn
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss install prompt"
              data-testid="pwa-install-dismiss"
              className="rounded-full p-1 -mt-1 -mr-1 hover:bg-[#F4F1EA]"
              style={{ color: "#94A3B8" }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-1 text-[12.5px] leading-snug" style={{ color: "#4B5162" }}>
            {iosHint ? (
              <>
                Tap the <span aria-hidden="true">↑</span> Share button in Safari and choose{" "}
                <b>Add to Home Screen</b> for faster access, offline mode, and a full app-like experience.
              </>
            ) : (
              <>Install SpectraLearn for faster access, offline use, important updates, and a full app-like experience right from your home screen.</>
            )}
          </p>
          <div className="mt-3 flex items-center gap-2">
            {!iosHint && (
              <button
                type="button"
                onClick={install}
                data-testid="pwa-install-accept"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-[13px] font-black text-white shadow-md hover:-translate-y-0.5 transition"
                style={{ background: "linear-gradient(180deg, #F97066 0%, #E5533E 100%)" }}
              >
                <Download className="h-4 w-4" /> Install
              </button>
            )}
            <button
              type="button"
              onClick={dismiss}
              data-testid="pwa-install-later"
              className="rounded-full px-4 py-2 text-[13px] font-bold hover:bg-[#F4F1EA]"
              style={{ color: "#4B5162" }}
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
